import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Clock, MapPin, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";
import { CallbackButton } from "./CallbackButton";
import { GoogleReviewsBadge } from "./GoogleReviewsBadge";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3f65efb9-d711-4fdc-a717-8b990519a5ff/images/d27807ae0f5aa9d5a9a0d0a5b86787be56adda7bd90f63f2cdc7188c751f4805.png";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden bg-gradient-to-b from-[#F4F7F6] via-white to-white"
    >
      <div className="absolute inset-x-0 top-0 h-[520px] pointer-events-none -z-10">
        <div className="absolute -top-32 -right-24 h-[420px] w-[420px] rounded-full bg-[#0284C7]/10 blur-3xl" />
        <div className="absolute top-20 -left-16 h-[340px] w-[340px] rounded-full bg-[#16A34A]/10 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-[#F0FDF4] border border-[#16A34A]/20 px-4 py-1.5 text-xs font-semibold text-[#16A34A] tracking-wide">
            <Sparkles size={14} />
            Schoonmaakbedrijf · Antwerpen · 24/7
          </div>

          <h1 className="mt-6 font-display font-extrabold tracking-tight text-[#0F172A] text-balance leading-[1.02]">
            <span className="block text-5xl sm:text-6xl lg:text-7xl">
              Schoonmaak van
            </span>
            <span className="block text-5xl sm:text-6xl lg:text-7xl">
              het <span className="text-[#0284C7]">hoogste niveau</span>
            </span>
            <span className="block text-5xl sm:text-6xl lg:text-7xl">
              in Antwerpen.
            </span>
          </h1>

          <p className="mt-7 max-w-xl font-body text-lg text-slate-600 leading-relaxed">
            Dila Group BV biedt 24/7 professionele schoonmaakdiensten voor
            bedrijven, kantoren en particulieren. Vlekkeloos werk, met oog voor
            detail.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              data-testid="hero-cta-quote"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold px-7 py-4 shadow-lg shadow-sky-200 transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              Offerte aanvragen
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <CallbackButton />
          </div>

          <div className="mt-5">
            <GoogleReviewsBadge compact />
          </div>

          <dl className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
            {[
              { icon: Clock, label: "Beschikbaar", value: "24 / 7" },
              { icon: MapPin, label: "Werkgebied", value: "Heel Antwerpen" },
              { icon: ShieldCheck, label: "Verzekerd", value: "100%" },
            ].map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase()}`} className="">
                <s.icon size={18} className="text-[#16A34A]" />
                <dt className="mt-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">{s.label}</dt>
                <dd className="font-display text-xl font-bold text-[#0F172A]">{s.value}</dd>
              </div>
            ))}
          </dl>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl shadow-sky-200/40 ring-1 ring-slate-200">
            <img
              src={HERO_IMG}
              alt="Smetteloos schoongemaakt interieur door Dila Group BV"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/30 via-transparent to-transparent" />
          </div>

          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 max-w-[240px] hidden sm:block">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#F0FDF4] flex items-center justify-center">
                <ShieldCheck size={20} className="text-[#16A34A]" />
              </div>
              <div>
                <p className="font-display text-sm font-bold text-[#0F172A]">Erkend & verzekerd</p>
                <p className="text-xs text-slate-500">Antwerpen, BE</p>
              </div>
            </div>
          </div>

          <div className="absolute -top-5 -right-5 bg-[#0F172A] text-white rounded-2xl shadow-xl px-5 py-3 hidden sm:block">
            <p className="font-display text-xs uppercase tracking-widest text-slate-300">Bel direct</p>
            <a href="tel:0466324304" className="font-display font-bold text-lg" data-testid="hero-phone-badge">
              0466 32 43 04
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
